/* eslint-disable @tencent/i18next/no-literal-string */
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { INumberTestManager, ModelRes, NumberType, ParaRes, RunRes, TextRes } from './number-test-manager.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IParagraph, ITextRun } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { splitTextByLanguage } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/language';

export default class NumberTestManager implements INumberTestManager {
    public result: ModelRes[] = [];

    private digitMap = new Map([
        ['零', 0],
        ['一', 1],
        ['壹', 1],
        ['贰', 2],
        ['二', 2],
        ['两', 2],
        ['三', 3],
        ['叁', 3],
        ['肆', 4],
        ['四', 4],
        ['伍', 5],
        ['五', 5],
        ['陆', 6],
        ['六', 6],
        ['柒', 7],
        ['七', 7],
        ['八', 8],
        ['捌', 8],
        ['九', 9],
        ['玖', 9],
    ]);
    private unitMap = new Map([
        ['十', 1],
        ['拾', 1],
        ['百', 2],
        ['佰', 2],
        ['千', 3],
        ['仟', 3],
        ['万', 4],
        ['亿', 8],
    ]);
    private negative = '负';
    private point = '点';
    private splitChar = ' .、。';

    public replaceSensitiveData(slideList: ISlideModel[], keepUnit = false) {
        this.searchNumber(slideList);
        this.result.forEach((modelRes) => {
            if (modelRes.model.type === UIModelType.TABLE) {
                this.replaceTableData(modelRes, keepUnit);
                return;
            }
            this.replaceShapeData(modelRes, keepUnit);
        });
        this.result = [];
    }

    public searchNumber(slideList: ISlideModel[]) {
        slideList.forEach((slide) => {
            slide.children.forEach((child) => {
                this.searchModelNumber(child);
            });
        });
    }

    public isValidChineseNumber(chineseNumber: string) {
        return !!this.getChineseNumberValue(chineseNumber);
    }

    private searchModelNumber(model: IBaseModel) {
        if (model?.type === UIModelType.GROUP) {
            model.children.forEach((child) => {
                this.searchModelNumber(child);
            });
            return;
        }
        if (model.type === UIModelType.TABLE) {
            this.searchTableNumber(model as ITableModel, this.result);
        }
        this.searchShapeNumber(model as IShapeModel, this.result);
    }

    private searchTableNumber(model: ITableModel, finalRes: ModelRes[]) {
        const { tableItems } = model;
        if (!tableItems.length) {
            return;
        }
        const tableRes: ModelRes[] = [];
        tableItems.forEach((row) => {
            row.forEach((item) => {
                this.searchShapeNumber(item, tableRes);
            });
        });
        if (tableRes.length) {
            finalRes.push({ model, modelRes: tableRes });
        }
    }

    private replaceTableData(tableModelRes: ModelRes, keepUnit: boolean) {
        const modelRes = tableModelRes.modelRes as ModelRes[];
        const table = tableModelRes.model as ITableModel;
        modelRes.forEach((result) => {
            this.replaceShapeData(result, keepUnit);
        });
        table.uploadAllCellData();
    }

    private replaceShapeData(modelRes: ModelRes, keepUnit: boolean) {
        const paraRes = modelRes.modelRes as ParaRes[];
        const shape = modelRes.model as IShapeModel;
        const newPara = shape.paragraphList?.map((para, paraIndex) => {
            const paraNeedReplace = paraRes.find(item => item.paraIndex === paraIndex);
            if (!paraNeedReplace) {
                return para;
            }
            const newTextRuns = this.getNewTextRuns(para, paraNeedReplace, keepUnit);
            return { ...para, textRuns: newTextRuns };
        });
        shape.updateParagraphListForUI(newPara);
        shape.updateParagraphList(newPara);
    }

    private getNewTextRuns(para: IParagraph, paraNeedReplace: ParaRes, keepUnit: boolean): ITextRun[] {
        const newTextRuns: ITextRun[] = [];
        para.textRuns?.forEach((run, runIndex) => {
            const runNeedReplace = paraNeedReplace.paraRes.find(item => item.runIndex === runIndex);
            if (!runNeedReplace || !run.text) {
                newTextRuns.push(run);
                return;
            }
            const { runRes } = runNeedReplace;
            let newText = '';
            let lastIndex = 0;
            runRes.forEach((textRes) => {
                if (!run.text) {
                    return;
                }
                const { textIndex, type, length } = textRes;
                newText = newText.concat(run.text.slice(lastIndex, textIndex));
                const numberText = run.text.slice(textIndex, length + textIndex);
                if (type === NumberType.CHINESE) {
                    newText = newText.concat(this.getChineseNewText(numberText, keepUnit));
                } else {
                    newText = newText.concat('*'.repeat(length));
                }
                lastIndex = length + textIndex;
            });
            newText = newText.concat(run.text.slice(lastIndex));
            splitTextByLanguage(newText).forEach((text) => {
                const lang = text.charAt(0) === '*' ? 'latin' : (run.styles?.lang || '');
                const styles = run.styles || {};
                styles.lang = lang;
                newTextRuns.push({
                    ...run,
                    styles,
                    text,
                });
            });
        });
        return newTextRuns;
    }

    private getChineseNewText(numberText: string, keepUnit: boolean) {
        let newText = '';
        if (this.isValidChineseNumber(numberText)) {
            if (keepUnit) {
                const maxUnitIndex = this.getMaxUnitIndex(numberText);
                let tempText = '';
                for (let i = 0;i < numberText.length;i++) {
                    if ((!maxUnitIndex && maxUnitIndex !== 0) || i < maxUnitIndex) {
                        tempText = `${tempText}*`;
                    } else if (i === maxUnitIndex) {
                        const char = numberText.charAt(i);
                        tempText = `${tempText}${char}`;
                    }
                }
                newText = tempText;
            } else {
                newText = '*'.repeat(numberText.length);
            }
        } else {
            newText = numberText;
        }
        return newText;
    }

    private getMaxUnitIndex(numberText: string): number | undefined {
        let maxUnitIndex;
        let maxUnit = 1;
        if (numberText.length <= 2 && this.getChineseNumberValue(numberText) < 20) {
            return;
        }
        for (let i = 0;i < numberText.length;i++) {
            const char = numberText.charAt(i);
            const unit = this.unitMap.get(char);
            if (unit && unit > maxUnit) {
                maxUnit = unit;
                maxUnitIndex = i;
            }
        }
        return maxUnitIndex;
    }

    private searchShapeNumber(model: IShapeModel, finalRes: ModelRes[]) {
        const { paragraphList } = model as IShapeModel;
        if (!paragraphList?.length) {
            return;
        }
        const result: ParaRes[] = [];
        paragraphList.forEach((para, paraIndex) => {
            const paraRes: RunRes[] = [];
            para.textRuns?.forEach((run, runIndex) => {
                const runRes: TextRes[] = [];
                this.getNumberInRun(run, runRes);
                if (runRes.length) {
                    paraRes.push({ runRes, runIndex });
                }
            });
            if (paraRes.length) {
                result.push({ paraRes, paraIndex });
            }
        });
        if (result.length) {
            this.clearSerialNumber(model, result);
            const modelRes: ModelRes = { model, modelRes: result };
            finalRes.push(modelRes);
        }
    }

    /**
     * 为了将序号的场景剔除掉，检测每个para的第一个有结果的run，如果其是第一个run且其数字结果与所属段的index相匹配则剃掉
     */
    private clearSerialNumber(model: IShapeModel, result: ParaRes[]) {
        if (result.length <= 1) {
            return;
        }
        result.forEach((paraRes) => {
            if (!(paraRes.paraRes[0].runIndex === 0 && paraRes.paraRes[0].runRes[0].textIndex === 0)) {
                return;
            }
            const text = model.paragraphList?.[paraRes.paraIndex]?.textRuns?.[0]?.text;
            if (!text) {
                return;
            }
            const [{ length }] = paraRes.paraRes[0].runRes;
            const numberValue = this.getNumberValue(text.slice(0, length));
            if (!numberValue) {
                return;
            }
            // 避免程序员们喜欢用零开头hhhh
            if (numberValue === paraRes.paraIndex || numberValue === paraRes.paraIndex + 1) {
                const nextChar = text.slice(length, length + 1)
                || model.paragraphList?.[paraRes.paraIndex]?.textRuns?.[1]?.text?.charAt(0);
                if (!nextChar || this.splitChar.indexOf(nextChar) === -1) {
                    return;
                }
                paraRes.paraRes[0].runRes.splice(0, 1);
            }
        });
    }

    private getNumberInRun(run: ITextRun, runRes: TextRes[]) {
        if (!run.text?.length) {
            return;
        }
        const lang = run.styles?.lang;
        for (let i = 0; i < run.text.length; i++) {
            const char = run.text.charAt(i);
            let numberLength = 0;
            let type = NumberType.ARABIC;
            if (lang === 'ea' && this.isChineseNumber(char)) {
                numberLength = this.getChineseNumberLength(i, run.text);
                type = NumberType.CHINESE;
            } else if (lang === 'latin' && this.isArabicNumber(char)) {
                numberLength = this.getArabicNumberLength(i, run.text);
            } else {
                if (this.isChineseNumber(char)) {
                    numberLength = this.getChineseNumberLength(i, run.text);
                    type = NumberType.CHINESE;
                } else if (this.isArabicNumber(char)) {
                    numberLength = this.getArabicNumberLength(i, run.text);
                }
            }
            if (numberLength) {
                runRes.push({ textIndex: i, length: numberLength, type });
                i += numberLength - 1;
            }
        }
    }

    private getChineseNumberLength(index: number, text: string, numberLength = 1): number {
        const char = text.charAt(index + 1);
        if (char.length && this.isChineseNumber(char)) {
            return this.getChineseNumberLength(index + 1, text, numberLength + 1);
        }
        return numberLength;
    }

    private getArabicNumberLength(index: number, text: string, numberLength = 1): number {
        const char = text.charAt(index + 1);
        if (char.length && this.isArabicNumber(char)) {
            return this.getArabicNumberLength(index + 1, text, numberLength + 1);
        }
        return numberLength;
    }

    private isArabicNumber(char: string) {
        return new RegExp('^\\d$').test(char);
    }

    private isChineseNumber(char: string) {
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        const chineseNumberList = '零一二两三四五六七八九十百千万亿';
        return chineseNumberList.indexOf(char) !== -1;
    }

    /**
     * 数组归一 (按索引覆盖合并数组,并清空被合并的数组)
     */
    private centerArray(baseArray, ...arrays) {
        return arrays.reduce((acc, cur) => {
            // 从基础数组的开头开始，删除和当前数组长度相同的元素数量
            acc.splice(0, cur.length);
            // 在基础数组的开头添加当前数组的所有元素
            acc.unshift(...cur);
            // 清空当前数组
            // eslint-disable-next-line no-param-reassign
            cur.length = 0;
            // 返回累积的结果
            return acc;
        }, baseArray);
    }

    /**
     * 向数组头部插入n个0
     */
    private unshiftZero(arr: (number|string)[], n: number) {
        if (n > 0) {
            arr.splice(0, 0, ...Array(n).fill(0));
        }
    }

    // eslint-disable-next-line complexity
    private getChineseNumberValue(chineseNumber: string): number {
        const input = chineseNumber.split(this.point);
        // 整数部分
        const int = input[0].replace(this.negative, '');
        // 小数部分
        const [, decimal] = input;

        // 十
        const ten = '十';

        let tempUnit = 0;
        let maxUnit = 0;
        const result: (string|number)[] = [];
        const currentValue: number[] = [];
        let tempValue: number[] = [];
        for (let i = 0; i < int.length; i++) {
            const chr = int.charAt(i);
            const digit = this.digitMap.get(chr);
            const unit = this.unitMap.get(chr);
            if (digit) {
                if (digit > 0) {
                    tempValue.unshift(digit);
                }
            } else if (unit) {
                if (tempUnit > unit) {
                    // 正常情况
                    this.unshiftZero(tempValue, unit);
                    this.centerArray(currentValue, tempValue);
                } else if (unit >= maxUnit) {
                    // 当前单位大于该段的最大单位，更新最大单位为当前单位
                    if (i === 0) {
                        tempValue = [1];
                    }
                    this.centerArray(result, currentValue, tempValue);
                    if (result.length > 0) {
                        this.unshiftZero(result, unit);
                    }
                    maxUnit = unit;
                } else {
                    if (tempValue.length === 0 && ten === chr) {
                        tempValue = [1];
                    }
                    this.centerArray(currentValue, tempValue);
                    this.unshiftZero(currentValue, unit);
                    tempUnit = unit;
                }
            }
        }
        this.centerArray(result, currentValue, tempValue).reverse();
        if (result.length === 0) {
            result.push(0);
        }
        if (decimal) {
            result.push('.');
            for (let i = 0; i < decimal.length; i++) {
                const decimalValue = this.digitMap.get(decimal.charAt(i));
                if (!decimalValue) {
                    break;
                }
                result.push(decimalValue);
            }
        }
        return parseFloat(result.join(''));
    }

    private getNumberValue(number: string): number {
        if (this.isArabicNumber(number.charAt(0))) {
            return Number(number);
        }
        return this.getChineseNumberValue(number);
    }
}
